/**
 * What has already been reported — VH-140.
 *
 * Reto trains across sessions rather than through them: three exercises from
 * session A, two from session B, five sets of pull-ups split over two visits.
 * The capture sheet only ever said "not logged" in faint grey per exercise, so
 * working out what was left meant opening every session and reading every block.
 *
 * No new backend state was needed for this, which is the useful part. Per
 * `exercise_capture_service.build_capture_sets`, the editable `actual_*` fields
 * come back as `null` until the exercise has been captured this session, and the
 * user's own saved numbers are returned once it has. So:
 *
 *     a set holding actual values IS a reported set
 *
 * The granularity was already in the payload and simply unused.
 *
 * `dirty` (unsaved local edits) is deliberately NOT treated as reported. A number
 * typed but not yet saved must never look like a number that is safely stored —
 * that is the one confusion that could cost a user their sets.
 */

/**
 * A set counts as reported when the user's own numbers are STORED against it.
 *
 * Must be given a server set (`block.sets`), never a row from the editable state.
 * `buildSetRows` pre-fills `actual_reps` from `target_reps_min` so the inputs
 * open with a number to beat — so an edit row ALWAYS looks filled, and reading
 * one here marked every set as reported on a session where nothing had been
 * captured at all. That is precisely the confusion this feature must not create:
 * a target is not an entry.
 */
export const isSetReported = (set) => {
  if (!set) return false
  // VH-186: a PREFILLED row is stored with `actual_weight`/`actual_reps`
  // copied from the best previous session. It is the target to beat, written
  // by `_prefill_sets_from_best` and re-written by `refresh_targets` days
  // later — nobody reported it. `is_prefill` is cleared the moment the user
  // actually saves the set, so it is the precise signal.
  //
  // VH-182 assumed `set_results` "holds only rows the user actually stored,
  // never pre-filled targets". That was simply not true, and it made every
  // session with prior history read as fully reported before it was opened.
  if (set.is_prefill) return false
  const filled = (v) => v !== null && v !== undefined && v !== ''
  return filled(set.actual_weight) || filled(set.actual_reps) || filled(set.actual_duration)
}

/**
 * Progress for one exercise block.
 *
 * Returns `{ state, reported, total }` where state is:
 *   'none'    — nothing stored yet
 *   'partial' — some sets stored, some not (the case that has no cue today)
 *   'full'    — everything stored
 *
 * For a non-set exercise (a hike, a class) there are no set rows, so the
 * exercise's own `is_captured` is the whole answer.
 */
/**
 * The same exercise arrives under two different shapes — VH-182.
 *
 * The capture sheet returns `sets` / `is_captured` (built by
 * `build_capture_sets`). The week payload behind "Report a session" returns the
 * ORM shape instead: `set_results` / `captured_at`. Reading only the capture
 * names against a week payload found neither, so every session in that list
 * reported "none" no matter what had been saved — which is exactly what Reto
 * saw for a week he had trained.
 *
 * `set_results` also differs in kind: it holds only rows the user actually
 * stored, never pre-filled targets, so the planned set count has to supply the
 * denominator or a half-logged exercise would read as complete.
 */
const asBlock = (block) => {
  if (!block) return { rows: [], captured: false, total: 0 }
  if (block.sets) {
    return { rows: block.sets, captured: !!block.is_captured, total: block.sets.length }
  }
  const rows = block.set_results ?? []
  const planned = (block.warmup_sets ?? 0) + (block.working_sets ?? 0)
  return {
    rows,
    captured: !!(block.is_captured ?? block.captured_at),
    total: Math.max(planned, rows.length),
  }
}

export const exerciseProgress = (block) => {
  // Deliberately ignores any caller-supplied rows: only the server payload can
  // distinguish a stored value from a pre-filled target.
  const { rows, captured, total } = asBlock(block)

  if (!total) {
    return { state: captured ? 'full' : 'none', reported: captured ? 1 : 0, total: 1 }
  }

  const reported = rows.filter(isSetReported).length

  // `is_captured` with no filled rows still counts as done: a metric-only
  // exercise, or one saved with genuinely empty sets, was reported by the user
  // even though there are no numbers to point at.
  if (reported === 0) {
    return { state: captured ? 'full' : 'none', reported: 0, total }
  }
  return {
    state: reported === total ? 'full' : 'partial',
    reported,
    total,
  }
}

/**
 * Progress for a whole session, for the Report-a-session list.
 *
 * The point of showing it there is deciding what is left BEFORE opening
 * anything, which is exactly what Reto could not do.
 */
export const sessionProgress = (blocks) => {
  const list = blocks ?? []
  if (!list.length) return { state: 'none', reported: 0, total: 0 }

  const states = list.map((b) => exerciseProgress(b).state)
  const reported = states.filter((s) => s === 'full').length
  const anyStarted = states.some((s) => s !== 'none')

  if (reported === list.length) return { state: 'full', reported, total: list.length }
  if (anyStarted) return { state: 'partial', reported, total: list.length }
  return { state: 'none', reported: 0, total: list.length }
}

export default exerciseProgress
